import { useEffect, useState } from 'react'
import HeroC from '../components/HeroC'
import { CORE_TEAM_DOMAIN, useAuth } from '../contexts/AuthContext'
import type { SponsorConfig } from '../shared/tokens/sponsor-config.types'
import {
  MASTER_AUDIENCE_FALLBACK,
  MASTER_HERO_SOCIAL_LINKS,
  buildAudienceSponsorConfig,
} from '../lib/masterAudience'

async function loadMasterAudienceConfig() {
  try {
    const res = await fetch('/api/public/sponsor-pages/master')
    const contentType = res.headers.get('content-type') ?? ''
    if (!contentType.includes('json')) throw new Error('audience_slice_unavailable')
    const data = await res.json() as { content?: SponsorConfig }
    if (!res.ok || !data.content) throw new Error('audience_slice_unavailable')
    return buildAudienceSponsorConfig(data.content)
  } catch {
    return buildAudienceSponsorConfig(MASTER_AUDIENCE_FALLBACK)
  }
}

function renderIntro(config: SponsorConfig) {
  const section = config.content.sections.find((item) => item.type === 'text')
  if (!section || section.type !== 'text') return null

  return (
    <section className="section-text">
      {section.heading && <h2>{section.heading}</h2>}
      {section.body.split(/\n{2,}/).map((paragraph, index) => (
        <p key={index}>{paragraph}</p>
      ))}
    </section>
  )
}

export default function MasterAudiencePage() {
  const { email } = useAuth()
  const [config, setConfig] = useState(() => buildAudienceSponsorConfig(MASTER_AUDIENCE_FALLBACK))
  const canUnlockAudiencePartnership = !!email && email.toLowerCase().endsWith(`@${CORE_TEAM_DOMAIN}`)

  useEffect(() => {
    let cancelled = false
    loadMasterAudienceConfig().then((nextConfig) => {
      if (!cancelled) setConfig(nextConfig)
    })
    return () => { cancelled = true }
  }, [])

  return (
    <main className="sponsor-page" data-surface="branding" data-sponsor="master" data-view-mode="audience">
      <HeroC
        headline={config.content.hero_headline}
        seasonLabel={config.content.hero_subhead}
        showCtaActions
        sponsorLogo={config.content.hero_sponsor_logo}
        sponsorName={config.content.hero_sponsor_name}
        mediaType={config.content.hero_video ? 'video' : 'image'}
        heroImageUrl={config.content.hero_poster || '/sunset-1.jpeg'}
        heroVideoUrl={config.content.hero_video || ''}
        heroVideoWebmUrl={config.content.hero_video_webm || ''}
        showScrollCue
        scrollTargetId="sponsor-content-start"
        partnershipDisabled={!canUnlockAudiencePartnership}
        partnershipDisabledLabel={canUnlockAudiencePartnership ? 'Read Partnership Opportunity' : 'Read Partnership Opportunity Locked'}
        socialLinks={MASTER_HERO_SOCIAL_LINKS}
      />

      <div id="sponsor-content-start" className="sponsor-content">
        {renderIntro(config)}
      </div>
    </main>
  )
}
